import { Badge, Card, DialogContainer, IconButton } from '@components'
import createEmblaCarousel from 'embla-carousel-solid'
import { Play } from 'lucide-solid'
import {
	type Accessor,
	For,
	createEffect,
	onCleanup,
	onMount,
	createSignal,
} from 'solid-js'
import { css, cx } from 'styled-system/css'
import { Box, Flex } from 'styled-system/jsx'
import { aspectRatio } from 'styled-system/patterns'
import { Loading } from '@components'

export const SetupImageCarousel = (props: {
	openImage: Accessor<number | undefined>
	setIsOpen: (value: number | undefined) => void
	images: { title: string; src?: string }[]
}) => {
	const [emblaRef, embla] = createEmblaCarousel(() => ({
		startIndex: props.openImage() ?? 0,
	}))

	const [isLoading, setIsLoading] = createSignal(false)
	let loadingTimeout: number | undefined

	// Add keyboard navigation
	onMount(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (props.openImage() === undefined) return // only active when dialog is open
			if (e.key === 'ArrowLeft') embla()?.scrollPrev()
			if (e.key === 'ArrowRight') embla()?.scrollNext()
		}

		window.addEventListener('keydown', handleKeyDown)
		onCleanup(() => window.removeEventListener('keydown', handleKeyDown))
	})

	// Scroll to selected image and show loader on open
	createEffect(() => {
		const openIndex = props.openImage()
		if (openIndex !== undefined && !Number.isNaN(openIndex)) {
			setIsLoading(true)
			if (loadingTimeout) clearTimeout(loadingTimeout)
			loadingTimeout = window.setTimeout(() => setIsLoading(false), 500)

			setTimeout(() => {
				embla()?.scrollTo(openIndex ?? 0, true)
			}, 300)
		} else {
			setIsLoading(false)
			if (loadingTimeout) {
				clearTimeout(loadingTimeout)
				loadingTimeout = undefined
			}
		}
	})

	return (
		<DialogContainer
			padding="0"
			isOpen={Boolean(
				props.openImage() !== undefined && !Number.isNaN(props.openImage()),
			)}
			setIsOpen={() => props.setIsOpen(undefined)}
		>
			{isLoading() ? (
				<Flex
					align="center"
					justify="center"
					height="100dvh"
					width="100vw"
				>
					<Loading />
				</Flex>
			) : (
				<Box
					position="relative"
					class={css({ overflow: 'hidden' })}
					ref={emblaRef}
				>
					<div
						class={css({
							display: 'flex',
							height: '100dvh',
							width: '100vw',
						})}
					>
						<For each={props.images}>
							{(image) => (
								<Box
									flex="0 0 100%"
									minWidth={0}
								>
									<Card.Root
										style={{
											'background-image': `url(${image.src})`,
										}}
										class={cx(
											aspectRatio({ ratio: 16 / 9 }),
											css({
												height: '100%',
												backgroundSize: 'contain',
												backgroundRepeat: 'no-repeat',
												backgroundPosition: 'center',
											}),
										)}
									/>
									<Box
										position="absolute"
										bottom={4}
										width="100%"
										textAlign="center"
									>
										<Badge
											variant="subtle"
											size="sm"
										>
											{image.title}
										</Badge>
									</Box>
								</Box>
							)}
						</For>
					</div>
					<Flex
						class={css({
							justifyContent: 'space-between',
							position: 'absolute',
							pointerEvents: 'none',
							top: 0,
							alignItems: 'center',
							width: '100%',
							height: '100%',
							left: 0,
							right: 0,
						})}
					>
						<IconButton
							pointerEvents="auto"
							colorPalette="tscGreen"
							variant="ghost"
							onClick={() => embla()?.scrollPrev()}
						>
							<Play
								class={css({
									transform: 'rotate(180deg)',
								})}
								fill="var(--colors-tsc-green-default)"
							/>
						</IconButton>
						<IconButton
							pointerEvents="auto"
							colorPalette="tscGreen"
							variant="ghost"
							onClick={() => embla()?.scrollNext()}
						>
							<Play fill="var(--colors-tsc-green-default)" />
						</IconButton>
					</Flex>
				</Box>
			)}
		</DialogContainer>
	)
}
